<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiAccordion - Design System Accordion Component
 *
 * A collapsible content section. Can be used standalone or in groups.
 *
 * @example
 * <UiAccordion title="Section Title">
 *   <p>Collapsible content here</p>
 * </UiAccordion>
 *
 * <UiAccordion title="FAQ Item" variant="minimal" :default-open="true">
 *   <p>Answer to the question</p>
 * </UiAccordion>
 */
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { IconLib } from '@/assets/icon-library';

export interface UiAccordionProps {
   /** Accordion title */
   title: string;
   /** Visual variant */
   variant?: 'default' | 'bordered' | 'minimal';
   /** Whether accordion is open by default */
   defaultOpen?: boolean;
   /** External control of open state */
   open?: boolean;
   /** Disable interaction */
   disabled?: boolean;
   /** Icon style for expand/collapse */
   iconStyle?: 'plus-minus' | 'chevron';
   /** Size variant */
   size?: 'sm' | 'md' | 'lg';
}

const props = withDefaults(defineProps<UiAccordionProps>(), {
   variant: 'default',
   defaultOpen: true,
   disabled: false,
   iconStyle: 'plus-minus',
   size: 'md',
});

const emit = defineEmits<{
   toggle: [isOpen: boolean];
}>();

// Detect controlled mode from the parent's raw props, not `props.open`: an
// absent Boolean `open` is coerced to `false`, indistinguishable from `false`.
const instance = getCurrentInstance();
const isControlled = computed(() => {
   const rawProps = instance?.vnode.props;
   return !!rawProps && 'open' in rawProps;
});

// Internal state (used when not externally controlled)
const internalOpen = ref(props.defaultOpen);

const isOpen = computed({
   get: () => (isControlled.value ? !!props.open : internalOpen.value),
   set: (value: boolean) => {
      if (!isControlled.value) {
         internalOpen.value = value;
      }
      emit('toggle', value);
   },
});

// Keep internal state in sync when a controlled parent changes `open`.
watch(() => props.open, (newVal) => {
   if (isControlled.value) {
      internalOpen.value = !!newVal;
   }
});

function toggle() {
   if (!props.disabled) {
      isOpen.value = !isOpen.value;
   }
}

// Container classes based on variant
const containerClasses = computed(() => {
   const base = 'w-full';

   switch (props.variant) {
      case 'bordered':
         return `${base} border border-black/10 rounded-xl overflow-hidden`;
      case 'minimal':
         return `${base}`;
      case 'default':
      default:
         return `${base} bg-neutral rounded-2xl overflow-hidden`;
   }
});

// Header classes based on variant and size
const headerClasses = computed(() => {
   const sizeClasses = {
      sm: 'px-3 py-2',
      md: 'px-4 py-4 md:px-6 md:py-5',
      lg: 'px-5 py-5 md:px-8 md:py-6',
   };

   const base = `flex gap-2 items-center w-full text-left ${sizeClasses[props.size]}`;

   if (props.disabled) {
      return `${base} cursor-not-allowed opacity-50`;
   }

   switch (props.variant) {
      case 'bordered':
         return `${base} hover:bg-neutral/50 cursor-pointer transition-colors`;
      case 'minimal':
         return `${base} hover:bg-neutral/30 cursor-pointer transition-colors`;
      case 'default':
      default:
         return `${base} cursor-pointer`;
   }
});

// Title classes based on size
const titleClasses = computed(() => {
   const sizeClasses = {
      sm: 'text-sm',
      md: 'text-base md:text-lg',
      lg: 'text-lg md:text-xl',
   };
   return `flex-1 font-semibold ${sizeClasses[props.size]}`;
});

// Content padding based on size
const contentClasses = computed(() => {
   const sizeClasses = {
      sm: 'px-3 pb-3',
      md: 'px-4 pb-4 md:px-6 md:pb-5',
      lg: 'px-5 pb-5 md:px-8 md:pb-6',
   };
   return sizeClasses[props.size];
});

// Icon classes based on size
const iconClasses = computed(() => {
   const sizeClasses = {
      sm: 'size-4',
      md: 'size-5 md:size-6',
      lg: 'size-6 md:size-7',
   };
   return `text-black ${sizeClasses[props.size]} transition-transform duration-200`;
});
</script>

<template>
   <div :class="containerClasses">
      <!-- Header / Toggle -->
      <button
         type="button"
         :class="headerClasses"
         :disabled="disabled"
         :aria-expanded="isOpen"
         @click="toggle"
      >
         <!-- Title slot or text -->
         <div :class="titleClasses">
            <slot name="title">
               {{ title }}
            </slot>
         </div>

         <!-- Icon -->
         <div class="shrink-0">
            <template v-if="iconStyle === 'plus-minus'">
               <Icon
                  v-if="isOpen"
                  :icon="IconLib.actions.minus"
                  :class="iconClasses"
               />
               <Icon
                  v-else
                  :icon="IconLib.actions.plus"
                  :class="iconClasses"
               />
            </template>
            <template v-else>
               <Icon
                  icon="ph:caret-down"
                  :class="[iconClasses, isOpen ? 'rotate-180' : '']"
               />
            </template>
         </div>
      </button>

      <!-- Content -->
      <Transition
         enter-active-class="transition-all duration-200 ease-out"
         enter-from-class="opacity-0 max-h-0"
         enter-to-class="opacity-100 max-h-[2000px]"
         leave-active-class="transition-all duration-150 ease-in"
         leave-from-class="opacity-100 max-h-[2000px]"
         leave-to-class="opacity-0 max-h-0"
      >
         <div v-if="isOpen" class="overflow-hidden">
            <div :class="contentClasses">
               <slot />
            </div>
         </div>
      </Transition>
   </div>
</template>
